<template>
  <b-table
    id="GeneralAdjustmentStockTable"
    ref="GeneralAdjustmentStockTable"
    small
    sort-icon-left
    head-variant="light"
    :items="items"
    :fields="fields"
  >
    <template v-slot:cell(index)="row">
      <div class="d-flex align-items-center justify-content-center">
        {{ row.index + 1 }}.
      </div>
    </template>

    <template v-slot:cell(image)="row">
      <ProductImage
        :src="row.item.productNoAndName?.imageUrl || null"
        :disabled="!row.item.productNoAndName"
      />
    </template>

    <template v-slot:cell(productNoAndName)="row">
      <MultipleSelectField
        :isRequired="true"
        v-model="row.item.productNoAndName"
        :options="getProductOptions(row.index)"
        trackBy="id"
        :customLabel="item => item.name || ''"
        placeholder="เลือกสินค้า"
        selectLabel="เลือกสินค้า"
        selectedLabel="เลือกแล้ว"
        deselectLabel="คลิกเพื่อลบออก"
        noResultLabel="ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา"
        :isError="
          getValidation(row.index) &&
          getValidation(row.index).productNoAndName.$error
        "
        @input="onProductNoAndNameChange(row)"
      />
    </template>

    <template v-slot:cell(adjustmentAmount)="row">
      <InputNumberField
        :adjustStock="row.item.adjustmentAmount"
        :onChange="value => onChangeAdjustStock(row, value)"
        :disabled="!row.item.productNoAndName"
        :invalid="
          submitAttempted &&
          getValidation(row.index) &&
          getValidation(row.index).adjustmentAmount.$error
        "
        :show-error-icon="false"
      />
    </template>

    <template v-slot:cell(currentStockToAfterAdjustment)="row">
      <PreAndPostStockField
        :currentStock="convertUnitStock(row)"
        :adjustStock="row.item.adjustmentAmount"
        :onChange="value => onChangeAdjustStock(row, value)"
        :disabled="!row.item.productNoAndName"
        :invalid="
          submitAttempted && isCurrentStockToAfterAdjustmentInvalid(row)
        "
        :show-error-icon="false"
      />
    </template>

    <template v-slot:cell(productUnit)="row">
      <MultipleSelectField
        :isRequired="true"
        v-model="row.item.productUnit"
        :options="buildUomOptions(row.item.productNoAndName)"
        trackBy="id"
        :customLabel="customLabel"
        placeholder="เลือกหน่วยสินค้า"
        selectLabel="เลือกหน่วยสินค้า"
        selectedLabel="เลือกแล้ว"
        deselectLabel=""
        noResultLabel="ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา"
        :disabled="!row.item.productNoAndName"
        :allowEmpty="false"
        :clearOnSelect="false"
        :show-error-icon="false"
        :isError="
          getValidation(row.index) &&
          getValidation(row.index).productUnit.$error
        "
        @input="onProductUnitChange(row)"
      />
    </template>

    <template v-slot:cell(buttons)="row">
      <button
        :disabled="row.index === 0 && items.length === 1"
        @click="$emit('delete-item', { item: row.item, index: row.index })"
        class="delete-btn"
      >
        <i class="fas fa-trash-alt"></i>
      </button>
    </template>

    <template v-slot:bottom-row>
      <td
        colspan="7"
        class="bottom-row-cell"
      >
        <b-button
          type="button"
          variant="outline-primary"
          @click="$emit('add-item')"
          class="bottom-add-row"
        >
          <i class="fas fa-plus"></i> เพิ่มรายการสินค้า
        </b-button>
      </td>
    </template>
  </b-table>
</template>

<script>
import BigNumber from "bignumber.js";
import PreAndPostStockField from "@/components/table/stock/fields/PreAndPostStockField.vue";
import InputNumberField from "@/components/table/stock/fields/InputNumberField.vue";
import MultipleSelectField from "@/components/table/stock/fields/MultipleSelectField.vue";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";

export default {
  name: "GeneralAdjustmentStockTable",
  components: {
    PreAndPostStockField,
    InputNumberField,
    MultipleSelectField,
    ProductImage,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    productList: {
      type: Array,
      default: () => [],
    },
    productOptions: {
      type: Array,
      default: () => [],
    },
    conversionUnitRate: {
      type: Array,
      default: () => [],
    },
    validation: {
      type: Object,
      default: () => ({}),
    },
    submitAttempted: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fields: [
        {
          key: "index",
          label: "",
          class: "text-center",
          thStyle: { width: "5%" },
          tdStyle: { width: "5%" },
        },
        { key: "image", label: "", thStyle: { width: "5%" } },
        {
          key: "productNoAndName",
          label: "รหัสสินค้า - ชื่อสินค้า",
          thStyle: { width: "30%" },
          tdStyle: { width: "30%" },
        },
        {
          key: "adjustmentAmount",
          label: "จำนวนที่ปรับปรุง",
          thStyle: { width: "15%" },
          tdStyle: { width: "15%" },
        },
        {
          key: "currentStockToAfterAdjustment",
          label: "สต๊อก (ปัจจุบัน→หลังปรับปรุง)",
          thStyle: { width: "20%" },
          tdStyle: { width: "20%" },
        },
        {
          key: "productUnit",
          label: "หน่วยสินค้า",
          thStyle: { width: "20%" },
          tdStyle: { width: "20%" },
        },
        {
          key: "buttons",
          label: "",
          class: "text-center",
          thStyle: { width: "5%" },
          tdStyle: { width: "5%" },
        },
      ],
    };
  },
  methods: {
    getProductOptions(currentIndex) {
      const selectedInOtherRows = new Set(
        this.items
          .map((item, i) =>
            i !== currentIndex && item.productNoAndName?.id
              ? item.productNoAndName.id
              : null
          )
          .filter(Boolean)
      );
      return this.productOptions.filter(
        product => !selectedInOtherRows.has(product.id)
      );
    },
    buildUomOptions(productNoAndName) {
      if (!productNoAndName) return [];
      const product = this.productList.find(
        p => p.productId === productNoAndName.id
      );
      if (!product) return [];
      const uoms = [];
      if (product.productUnit) {
        uoms.push({
          id: product.productUnit.id,
          name: product.productUnit.description,
        });
      }
      if (product.productConversionUnitItems?.length) {
        product.productConversionUnitItems.forEach(item => {
          uoms.push({ id: item.uom.id, name: item.uom.description });
        });
      }
      return uoms.filter(
        (uom, index, self) => index === self.findIndex(t => t.id === uom.id)
      );
    },
    customLabel(item) {
      return item?.name || "";
    },
    convertUnitStock(row) {
      return row.item.productAvailableStock;
    },
    onProductNoAndNameChange(row) {
      row.item.productUnit = null;
      if (row.item.productNoAndName?.id) {
        const product = this.productList.find(
          p => p.productId === row.item.productNoAndName.id
        );

        if (product.productUnit) {
          row.item.productUnit = {
            id: product.productUnit.id,
            name: product.productUnit.description,
          };
          const newRates = [{ id: product.productUnit.id, rate: 1 }];
          if (product.productConversionUnitItems?.length) {
            product.productConversionUnitItems.forEach(item => {
              const qty = new BigNumber(item.qtyPerUnitOfMeasure || 0);
              if (!qty.isFinite() || qty.isZero()) return;
              const rate = new BigNumber(1)
                .div(qty)
                .decimalPlaces(2, BigNumber.ROUND_HALF_UP);
              newRates.push({ id: item.uom.id, rate: rate.toNumber() });
            });
          }
          this.$emit("update-conversion-rate", newRates);
        }

        const available = product?.productAvailableStock ?? 0;
        row.item.productAvailableStock = available;
        this.$set(row.item, "adjustmentAmount", 0);
        this.$set(row.item, "currentStockToAfterAdjustment", available);
      } else {
        row.item.productAvailableStock = undefined;
        this.$set(row.item, "adjustmentAmount", 0);
        row.item.currentStockToAfterAdjustment = undefined;
      }

      const rowV = this.getValidation(row.index);
      if (rowV?.productUnit?.$reset) rowV.productUnit.$reset();
      if (rowV?.adjustmentAmount?.$reset) rowV.adjustmentAmount.$reset();
      if (rowV?.currentStockToAfterAdjustment?.$reset)
        rowV.currentStockToAfterAdjustment.$reset();
    },
    onProductUnitChange(row) {
      if (row.item.productUnit?.id) {
        const product = this.productList.find(
          p => p.productId === row.item.productNoAndName.id
        );
        const available = product?.productAvailableStock ?? 0;
        const conversionRate = this.conversionUnitRate.find(
          r => r.id === row.item.productUnit.id
        );
        if (conversionRate) {
          const availableAfterConversion = available * conversionRate.rate;
          row.item.productAvailableStock = availableAfterConversion;
          this.$set(row.item, "adjustmentAmount", 0);
          this.$set(
            row.item,
            "currentStockToAfterAdjustment",
            availableAfterConversion
          );
        }
      } else {
        row.item.productAvailableStock = undefined;
        this.$set(row.item, "adjustmentAmount", 0);
        row.item.currentStockToAfterAdjustment = undefined;
      }
    },
    onChangeAdjustStock(row, value) {
      const num = Number(value);
      this.$set(row.item, "adjustmentAmount", num);
      const current = Number(row.item.productAvailableStock) || 0;
      const afterAdjustment = new BigNumber(current).plus(num).toFixed(2);
      this.$set(row.item, "currentStockToAfterAdjustment", afterAdjustment);

      const rowV = this.getValidation(row.index);
      if (rowV?.adjustmentAmount?.$reset) rowV.adjustmentAmount.$reset();
      if (rowV?.currentStockToAfterAdjustment?.$reset)
        rowV.currentStockToAfterAdjustment.$reset();
    },
    getValidation(index) {
      try {
        return (this.validation && this.validation[index]) || null;
      } catch (e) {
        return null;
      }
    },
    isCurrentStockToAfterAdjustmentInvalid(row) {
      const rowV = this.getValidation(row.index);
      return !!(
        rowV &&
        rowV.currentStockToAfterAdjustment &&
        rowV.currentStockToAfterAdjustment.$error
      );
    },
  },
};
</script>
